export interface ParsedInfluencer {
  handle: string;
  platform: string;
  displayName?: string | undefined;
  emails?: string[] | undefined;
  countrySignals?: string[] | undefined;
  profileUrl?: string | undefined;
}

// Maps raw CSV header (lowercased, trimmed) → internal field key
const HEADER_ALIASES: Record<string, string> = {
  // TikTok handle / URL
  "tiktok account url": "tiktokUrl",
  "tiktok url": "tiktokUrl",
  handle: "tiktokUrl",
  username: "tiktokUrl",
  "social media handle": "tiktokUrl",
  // Other social
  "other social media account url": "otherSocialUrl",
  "other social media url": "otherSocialUrl",
  "instagram url": "otherSocialUrl",
  // Name fields
  "first name": "firstName",
  "last name": "lastName",
  "display name": "displayName",
  displayname: "displayName",
  name: "displayName",
  "influencer name": "displayName",
  "creator name": "displayName",
  // Email
  "active e mail address": "email",
  "active email address": "email",
  "active email": "email",
  email: "email",
  "email address": "email",
  emails: "email",
  // Country / Region
  country: "country",
  region: "country",
  location: "country",
  // Followers
  "total tiktok followers": "followers",
  followers: "followers",
  // Platform (generic)
  platform: "platform",
  // Profile URL (generic)
  "profile url": "profileUrl",
  "social media url": "profileUrl",
  url: "profileUrl",
  link: "profileUrl",
};

function normalizeHeader(header: string): string | null {
  const cleaned = header
    .trim()
    .toLowerCase()
    .replace(/[_\-]/g, " ")
    .replace(/[^\w\s]/g, "") // strip emoji and special chars
    .replace(/\s+/g, " ")
    .trim();
  return HEADER_ALIASES[cleaned] ?? null;
}

function parseRow(line: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === "\t" || char === ",") {
        fields.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
  }
  fields.push(current.trim());
  return fields;
}

function extractHandleFromUrl(url: string): { handle: string; platform: string } | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    const pathParts = u.pathname.split("/").filter(Boolean);
    if (pathParts.length === 0) return null;

    let handle = pathParts[pathParts.length - 1] ?? "";
    handle = handle.replace(/^@/, "");

    const platformMap: Record<string, string> = {
      "tiktok.com": "tiktok",
      "instagram.com": "instagram",
      "youtube.com": "youtube",
      "x.com": "twitter",
      "twitter.com": "twitter",
      "facebook.com": "facebook",
      "snapchat.com": "snapchat",
      "twitch.tv": "twitch",
      "pinterest.com": "pinterest",
      "linkedin.com": "linkedin",
    };

    const platform = platformMap[host];
    if (!platform) return null;
    return { handle, platform };
  } catch {
    return null;
  }
}

/**
 * Extract country code from formats like "US (United States)" → "US"
 */
function parseCountry(raw: string): string {
  const trimmed = raw.trim().toUpperCase();
  const match = trimmed.match(/^([A-Z]{2})\s*\(/);
  if (match) return match[1] ?? "";
  if (/^[A-Z]{2}$/.test(trimmed)) return trimmed;
  return "";
}

export interface CsvParseResult {
  data: ParsedInfluencer[];
  errors: string[];
  headers: string[];
}

export function parseCsv(content: string, defaultPlatform: string = "tiktok"): CsvParseResult {
  const lines = content.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    return { data: [], errors: ["CSV file is empty or has no data rows"], headers: [] };
  }

  const rawHeaders = parseRow(lines[0] ?? "");
  const mappedHeaders = rawHeaders.map(normalizeHeader);
  const errors: string[] = [];
  const data: ParsedInfluencer[] = [];

  for (let i = 1; i < lines.length; i++) {
    const fields = parseRow(lines[i] ?? "");
    if (fields.every((f) => !f)) continue; // skip empty rows

    const row: Record<string, string> = {};
    mappedHeaders.forEach((key, idx) => {
      if (key && fields[idx]) row[key] = fields[idx];
    });

    // --- Resolve handle & platform ---
    let handle = "";
    let platform = row["platform"]?.toLowerCase() ?? defaultPlatform;
    let profileUrl = row["profileUrl"] ?? "";

    // 1) Try TikTok URL column (can be a full URL or just a handle)
    const tiktokRaw = row["tiktokUrl"] ?? "";
    if (tiktokRaw) {
      if (tiktokRaw.startsWith("http")) {
        const extracted = extractHandleFromUrl(tiktokRaw);
        if (extracted) {
          handle = extracted.handle;
          platform = extracted.platform;
          profileUrl = tiktokRaw;
        }
      } else {
        // Raw handle text (e.g. "longislandicedtwea" or "Cjeezy77")
        handle = tiktokRaw.replace(/^@/, "");
        platform = "tiktok";
      }
    }

    // 2) Fallback: try generic profileUrl column
    if (!handle && profileUrl) {
      const extracted = extractHandleFromUrl(profileUrl);
      if (extracted) {
        handle = extracted.handle;
        platform = extracted.platform;
      }
    }

    if (!handle) {
      errors.push(`Row ${i + 1}: missing handle`);
      continue;
    }

    // --- Resolve displayName ---
    let displayName = row["displayName"];
    if (!displayName && (row["firstName"] || row["lastName"])) {
      displayName = [row["firstName"], row["lastName"]].filter(Boolean).join(" ");
    }

    // --- Resolve country ---
    let countrySignals: string[] | undefined;
    if (row["country"]) {
      countrySignals = [parseCountry(row["country"])];
    }

    const influencer: ParsedInfluencer = {
      handle,
      platform,
      displayName: displayName || undefined,
      emails: row["email"] ? [row["email"]] : undefined,
      countrySignals,
      profileUrl: profileUrl || undefined,
    };

    data.push(influencer);
  }

  return { data, errors, headers: rawHeaders };
}
